import type { Vec3 } from './assemblyPose';
import { loadDeps } from './meshScene';
import { mmScale } from './meshAnnotations';
import { builtBoundsOf, builtBoxOf } from './openaiBlenderApi';
import type { PartItem } from './partsRun';
import { editsOf, isTrueScale, jobExtents, meshAtSrc, meshSrc, meshStamp, undrilledAt, type Job3D } from './threeDApi';

// three-mesh-bvh, imported when a solid is first loaded (see assemblyClash, which does the same).
let bvhMod: Promise<typeof import('three-mesh-bvh')> | null = null;
const loadBvh = () => (bvhMod ??= import('three-mesh-bvh'));

// A PART'S REAL SURFACE, FOR A LINE TO MEET — see holeLines.
//
// A box is where a part COULD be; a frame's box is mostly air, and a leg slanting back leaves its
// own box's face well clear of the wood. A hole's depth counts from where the drill touches wood
// (the drill itself measures that way — see drill_hole), so the line has to know the same thing:
// where the screw's head bears on the leg, and where its shank first meets the rail.
//
// THE KEPT MESH, IN MILLIMETRES, WHERE THE PRODUCT BUILT IT. Loaded raw, scaled by the mmScale
// every size in the app goes through, and moved so its box lands on the box its measures give in
// the product's frame (builtBoundsOf). Its axes are the product's: every mesh is authored so.

// Every distance along a ray at which it crosses the part's surface, in order — going in, coming
// out, going in again past a notch.
export type Solid = (from: Vec3, dir: Vec3) => number[];

// THE KEPT MESH, LOADED AND PLACED: in millimetres, standing where the product built it. Null
// where its scale or its place in the product is unknown. Shared by the solids below and the
// joint view, which draws the parts of a joint together.
//
// `undrilled`: AS IT STOOD BEFORE ITS FIRST BORE (see undrilledAt), where a saved state is that — what
// a re-run would be shown and measure on. `drilled` says it fell back to the mesh as it is.
export async function loadPlacedMesh(job: Job3D, size: PartItem['size'] | null | undefined, undrilled = false) {
  const bounds = builtBoundsOf(job);
  const raw = await jobExtents(job.id);
  const k = raw ? mmScale(raw, size ?? null, isTrueScale(job), builtBoxOf(job)) : null;
  if (!bounds || !k) return null;
  const { THREE, GLTFLoader } = await loadDeps();
  const at = undrilled ? undrilledAt(job) : null;
  const past = at != null && at < editsOf(job).length;
  const gltf = await new GLTFLoader().loadAsync(past ? meshAtSrc(job.id, at, meshStamp(job)) : meshSrc(job.id, meshStamp(job)));
  const root = gltf.scene;
  root.scale.setScalar(k);
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  root.position.set(bounds.min[0] - box.min.x, bounds.min[1] - box.min.y, bounds.min[2] - box.min.z);
  root.updateMatrixWorld(true);
  return { THREE, root, drilled: undrilled && at == null };
}

export async function loadSolid(job: Job3D, size: PartItem['size'] | null | undefined, undrilled = false): Promise<Solid | null> {
  const placed = await loadPlacedMesh(job, size, undrilled);
  if (!placed) return null;
  const { THREE, root } = placed;
  const { MeshBVH, acceleratedRaycast } = await loadBvh();
  // BOTH SIDES OF EVERY TRIANGLE: a ray leaving the wood meets a face from behind, and that
  // crossing is half of what is asked. AND A TREE OVER THEM: a slot's allowed area is a few hundred
  // lines through two meshes of a hundred thousand triangles each (see holeArea). This copy of the
  // mesh is the solid's own, so the tree may reorder its index.
  root.traverse((o) => {
    const mesh = o as import('three').Mesh;
    if (!mesh.isMesh) return;
    for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) m.side = THREE.DoubleSide;
    (mesh.geometry as unknown as { boundsTree?: unknown }).boundsTree = new MeshBVH(mesh.geometry);
    mesh.raycast = acceleratedRaycast;
  });
  const caster = new THREE.Raycaster();
  return (from, dir) => {
    caster.set(new THREE.Vector3(...from), new THREE.Vector3(...dir).normalize());
    caster.far = Infinity;
    // A seam's two triangles answer the same crossing twice; one tenth of a millimetre apart is one.
    const out: number[] = [];
    for (const hit of caster.intersectObject(root, true)) {
      if (!out.length || hit.distance - out[out.length - 1] > 0.1) out.push(hit.distance);
    }
    return out;
  };
}

// EACH PART'S SOLID, loaded once into `into` — what a group's lines are measured on. A part whose mesh
// will not load is left out, and measured on its box, said (see holeLines). `tried` keeps one that
// failed from being fetched again.
export async function loadSolids(
  ids: string[],
  meshOf: ((id: string) => Job3D | null) | undefined,
  items: PartItem[],
  into: Map<string, Solid>,
  tried: Set<string> = new Set(),
  undrilled = false,
): Promise<Map<string, Solid>> {
  for (const id of ids) {
    if (into.has(id) || tried.has(id)) continue;
    tried.add(id);
    const job = meshOf?.(id);
    try {
      const solid = job ? await loadSolid(job, items[Number(id)]?.size, undrilled) : null;
      if (solid) into.set(id, solid);
    } catch {
      // Measured on its box, and said so — see holeLines.
    }
  }
  return into;
}
